// @ts-check
/* eslint-env browser */

/** @type {import('../reactive-store').reactiveStore} */
/** @typedef {import('@geoman-io/maplibre-geoman-free').GeoJsonImportFeature} GeoJsonImportFeature */

import ml from 'maplibre-gl'
import { getGeomanStyle } from './geoman-style'
import { reactiveStore } from '../reactive-store'
import * as turf from '@turf/turf'
import { editFeature } from './edit-feature'
import { mergeGeometriesForAddMode } from './add-geometries'
import { Snapping } from './snapping'
import { MagicWand } from './magic-wand'
import { initGeoTIFFs, syncGeoTIFFOverlaysFromFeatures, updateGeoTIFFOpacities, clearOverlayCornerCache, moveGeoTIFFsBeforeGeoman } from './geotiff-overlay'
import { applyGroupTransform, trackRotation, applyAccumulatedRotation, getRotationState } from './overlay-group-edit'

export class BaseMap extends HTMLElement {
  #hiddenOverlayIds = new Set()
  /** @type {number|null} */ #rafId = null
  /** @type {(() => void)|null} */ #mouseLeaveListener = null
  /** @type {string|null} */ #lastCutGeometry = null

  connectedCallback () {
    this.#initMaplibre()
    this.#initGeoman()

    reactiveStore.features.subscribe((features, message) => {
      // Skip re-importing features when GCPs are updated (avoids triggering events that cause flash)
      if (message !== 'gcp-update') {
        this.#showFeatures(features)
      }
      if (message === 'undo-redo' && this.map) {
        syncGeoTIFFOverlaysFromFeatures(this.map, features, true, null, true)
        // Reset cut geometry cache after undo/redo to allow re-cutting to same geometry
        this.#lastCutGeometry = null
      }
    })

    reactiveStore.overlayMaps.subscribe((overlayMaps) => {
      if (this.map && this.geoman) {
        updateGeoTIFFOpacities(this.map, overlayMaps)
        const nextHiddenOverlayIds = new Set(
          overlayMaps
            .filter(({ opacity }) => opacity === 0)
            .map(({ storagePath }) => storagePath)
        )
        const visibilityChanged =
          nextHiddenOverlayIds.size !== this.#hiddenOverlayIds.size ||
          [...nextHiddenOverlayIds].some(id => !this.#hiddenOverlayIds.has(id))
        if (visibilityChanged) {
          this.#hiddenOverlayIds = nextHiddenOverlayIds
          this.#showFeatures(reactiveStore.features.get())
        }
      }
    })

    reactiveStore.selectedFeatureId.subscribe((id) => {
      if (this.geoman && this.map) {
        editFeature(this.geoman, this.map, id, reactiveStore.editMode.get())
        // Re-enable snapping if it was enabled (editFeature calls disableAllModes which disables it)
        if (reactiveStore.snap.get()) {
          this.#handleSnapChange(true)
        }
      }
    })

    reactiveStore.editMode.subscribe((mode) => {
      if (!this.geoman || !this.map) {
        return
      }
      // Reset cut geometry cache when exiting cut mode
      if (mode !== 'cut') {
        this.#lastCutGeometry = null
      }
      editFeature(this.geoman, this.map, reactiveStore.selectedFeatureId.get(), mode)
      // Re-enable snapping if it was enabled (editFeature calls disableAllModes which disables it)
      if (reactiveStore.snap.get()) {
        this.#handleSnapChange(true)
      }
      // Enable/disable magic wand based on mode
      if (mode === 'magic_wand') {
        this.magicWand?.enable()
      } else {
        this.magicWand?.disable()
      }

      const isDragTransformMode = ['rotate', 'drag', 'scale', 'change'].includes(mode)

      // Keep GeoTIFF in sync with overlay polygon while user drags the rotate handle
      if (mode === 'rotate') {
        this.#startRotateSync()
      } else {
        this.#stopRotateSync()
      }

      // Set up mouseleave handler for all drag/transform modes
      if (isDragTransformMode) {
        this.#setupMouseLeaveHandler()
      } else {
        this.#removeMouseLeaveHandler()
      }
    })

    reactiveStore.tileType.subscribe((tileType) => {
      this.#changeTileType(tileType)
    })
  }

  #initMaplibre (styleUrl) {
    // Use provided style URL or get from reactiveStore
    const style = styleUrl || (reactiveStore.tileType.get() === 'satellite' ? '/api/map-style-satellite' : '/api/map-style-os')

    if (!this.id) {
      this.id = `map-${crypto.randomUUID().substring(0, 8)}`
    }

    this.map = new ml.Map({
      container: this.id,
      style,
      center: [-3, 53],
      zoom: 7
    })

    // load tree marker icons
    this.map.loadImage(`${window.location.origin}/public/icons/tree-icon-blue.webp`).then((image) => {
      this.map?.addImage('tree-icon-blue', image.data)
    })
    this.map?.loadImage(`${window.location.origin}/public/icons/tree-icon-red.webp`).then((image) => {
      this.map?.addImage('tree-icon-red', image.data)
    })

    this.map.on('click', (e) => {
      // Skip click handling when in "add", "cut" or "magic_wand" mode to allow those modes to handle clicks
      const editMode = reactiveStore.editMode.get()
      if (editMode === 'add' || editMode === 'add_point' || editMode === 'cut' || editMode === 'magic_wand') {
        return
      }
      const features = this.map?.queryRenderedFeatures(e.point)
        .filter(f => !f.layer?.id?.startsWith('geotiff-layer-'))
        .filter(f => f.properties?._id)

      const nonOverlayFeatures = features.filter(f => !f.properties?._isMapOverlay)
      const featureToSelect = nonOverlayFeatures.length > 0 ? nonOverlayFeatures[0] : features[0]

      if (featureToSelect) {
        reactiveStore.selectedFeatureId.set(featureToSelect.properties?._id)
      } else {
        reactiveStore.selectedFeatureId.set('')
      }
    })
  }

  async #initGeoman () {
    if (!this.map) {
      return
    }

    // Dynamically import Geoman (Pro if available, Free otherwise)
    const geomanProAvailable = this.getAttribute('geoman-pro-available') === 'true'

    let Geoman
    if (geomanProAvailable) {
      try {
        const geomanPro = await import('@geoman-io/maplibre-geoman-pro')
        Geoman = geomanPro.Geoman
      } catch (error) {
        console.warn('Geoman Pro not available, falling back to Free version:', error.message)
        const geomanFree = await import('@geoman-io/maplibre-geoman-free')
        Geoman = geomanFree.Geoman
      }
    } else {
      const geomanFree = await import('@geoman-io/maplibre-geoman-free')
      Geoman = geomanFree.Geoman
    }

    // callback when geoman is fully loaded
    this.map.once('gm:loaded', () => {
      // Let other components know that geoman is loaded (so features are set before initGeoTIFFs)
      reactiveStore.mapReady.set(true)

      // Initialise snapping
      if (this.map && this.geoman) {
        this.snapping = new Snapping(this.map)
        this.#handleSnapChange(reactiveStore.snap.get())
      }

      // Initialise magic wand
      if (this.map && this.geoman) {
        this.magicWand = new MagicWand(this.map, this.geoman)
        // Enable if already in magic_wand mode
        if (reactiveStore.editMode.get() === 'magic_wand') {
          this.magicWand.enable()
        }
      }

      // Subscribe to snap changes
      this.snapCallback = (snap) => {
        this.#handleSnapChange(snap)
      }
      reactiveStore.snap.subscribe(this.snapCallback)

      // Initialise overlay map settings from window.mapOverlays if needed
      if (reactiveStore.overlayMaps.get().length === 0) {
        const overlays = window['mapOverlays'] || [] // eslint-disable-line dot-notation
        reactiveStore.overlayMaps.set(
          overlays.map((overlay) => ({
            storagePath: overlay.storage_path,
            opacity: typeof overlay.opacity === 'number' ? overlay.opacity : 0.7,
            moveWith: true
          }))
        )
      }

      // Add GeoTIFF overlays based on overlay opacities
      if (this.map) {
        const overlayMaps = reactiveStore.overlayMaps.get()
        initGeoTIFFs(this.map, overlayMaps)
        // Ensure handles/visibility match current opacities (e.g. after tile-type change)
        updateGeoTIFFOpacities(this.map, overlayMaps)
        // Ensure GeoTIFF footprint matches the currently loaded editable overlay polygons.
        syncGeoTIFFOverlaysFromFeatures(this.map, reactiveStore.features.get(), false, null)
      }

      // Listen for map edit events and save features back to reactiveStore
      this.geoman?.setGlobalEventsListener((parameters) => {
        const eventName = String(parameters.name || '')

        // Handle feature updates from cut operations using _gm:edit:feature_updated (as gm:cut wasn't firing correctly)
        // Only process this event when in cut mode to avoid duplicate updates for other edit modes
        if (parameters.name === '_gm:edit:feature_updated') {
          if (reactiveStore.editMode.get() === 'cut') {
            this.#saveCutFeature()
          }
          return
        }

        // The CUT handler isn't reliably triggered by Geoman when the entire geometry is removed, so we handle that case here
        if (parameters.name === '_gm:edit:feature_removed') {
          const selectedId = reactiveStore.selectedFeatureId.get()
          if (!selectedId) return

          // Check if selected feature still exists in featureStore
          // (if it exists, the removed feature was something else e.g. a tree)
          let selectedFeatureStillExists = false
          this.geoman?.features.featureStore.forEach((entry) => {
            try {
              const geoJson = entry.getGeoJson()
              if (geoJson?.properties?._id === selectedId) {
                selectedFeatureStillExists = true
              }
            } catch (error) {}
          })

          if (selectedFeatureStillExists) {
            // Tree or other feature was removed, but selected polygon still exists
            // Try to save the cut (feature_updated may not have fired)
            this.#saveCutFeature()
            return
          }

          // Selected feature was actually removed (entire polygon cut away)
          const allFeatures = reactiveStore.features.get()
          const updatedFeatures = allFeatures.map(feature =>
            feature.properties?._id === selectedId
              ? { ...feature, geometry: { type: 'Polygon', coordinates: [] } }
              : feature
          )
          reactiveStore.features.set(updatedFeatures)
          return
        }

        // Track accumulated rotation increments for contained-feature transforms (fires incrementally during rotation)
        // GeoTIFF sync during rotation is handled separately by the RAF loop in #startRotateSync
        if (parameters.name === 'gm:rotate') {
          const selectedId = reactiveStore.selectedFeatureId.get()
          if (selectedId) {
            trackRotation(selectedId, this.#collectLiveFeatures())
          }
          return
        }

        // Live preview: update GeoTIFF image as overlay is dragged/edited/scaled
        if (['gm:drag', 'gm:edit', 'gm:scale'].includes(parameters.name)) {
          if (this.map) {
            syncGeoTIFFOverlaysFromFeatures(this.map, this.#collectLiveFeatures(), false, null)
          }
          return
        }

        const editEvents = [
          'gm:dragend',
          'gm:editend',
          'gm:rotateend',
          'gm:scaleend',
          '_gm:draw:feature_created',
          'gm:draw:feature_created',
          'gm:magicwand'
        ]
        if (!editEvents.includes(parameters.name)) {
          return
        }
        // merge geometries if editMode === 'add' or 'add_point'
        // @ts-ignore - featureData exists on draw:feature_created events
        if (this.geoman && mergeGeometriesForAddMode(this.geoman, eventName, parameters.featureData)) {
          // Geometry was added to reactiveStore - showFeatures will be triggered by the subscription
          // Use setTimeout to ensure showFeatures has completed before enabling edit mode
          const selectedId = reactiveStore.selectedFeatureId.get()
          setTimeout(() => {
            // Re-filter edit controls first (feature now has geometry, tools will change)
            const editControls = document.querySelector('edit-controls')
            if (editControls && typeof editControls.refreshOptionsForCurrentFeature === 'function') {
              editControls.refreshOptionsForCurrentFeature()
            }

            // Re-enable the current edit mode (for 'add' mode to allow adding more geometry)
            const modeToEnable = reactiveStore.editMode.get()
            editFeature(this.geoman, this.map, selectedId, modeToEnable)
          }, 0)

          return // Skip normal feature collection
        }

        // Collect all features from Geoman
        const features = this.#collectLiveFeatures()

        // Add back any features that aren't in Geoman (hidden overlays, null-geometry features, etc.)
        const currentFeatures = reactiveStore.features.get()

        // Hidden overlays (opacity 0)
        const hiddenOverlays = currentFeatures.filter(f =>
          f?.properties?._isMapOverlay && this.#hiddenOverlayIds.has(f.properties._id)
        )
        features.push(...hiddenOverlays)

        // Null-geometry features (can't be rendered on map but must be preserved)
        const nullGeometryFeatures = currentFeatures.filter(f =>
          !f?.properties?._isMapOverlay &&
          (!f?.geometry || !f.geometry.coordinates?.length)
        )
        features.push(...nullGeometryFeatures)

        // Move contained features with the overlay when it is dragged/rotated/edited
        applyGroupTransform(features, parameters.name)
        // Capture rotation state before applyAccumulatedRotation clears it
        const rotationContext = parameters.name === 'gm:rotateend' ? getRotationState() : null
        // Apply accumulated rotation for rotateend (instead of using bearing comparison)
        applyAccumulatedRotation(features, parameters.name)
        // Sync GeoTIFF before recording history so GCP snapshots match the polygon.
        if (this.map) {
          syncGeoTIFFOverlaysFromFeatures(this.map, features, true, rotationContext)
        }
        reactiveStore.features.set(features)
      })
    })

    // init geoman with style based on current tile type
    const geomanStyle = getGeomanStyle(reactiveStore.tileType.get())
    this.geoman = new Geoman(this.map, geomanStyle)

    // Setup custom layer ordering for overlay vs regular features
    this.#setupFeatureLayerOrdering()
  }

  /**
   * Sets up custom MapLibre layers to control rendering order of overlay vs regular features.
   * Adds filters to Geoman's default layers to exclude overlays, then creates separate overlay layers.
   */
  #setupFeatureLayerOrdering () {
    if (!this.map) return

    // Wait for Geoman to fully initialize and add its layers
    this.map.once('gm:loaded', () => {
      const style = this.map.getStyle()
      if (!style) return

      // Find Geoman's default polygon layers for gm_main source
      const geomanFillLayer = style.layers?.find(layer =>
        layer.source === 'gm_main' && layer.type === 'fill'
      )
      const geomanLineLayer = style.layers?.find(layer =>
        layer.source === 'gm_main' && layer.type === 'line'
      )

      if (!geomanFillLayer || !geomanLineLayer) {
        console.warn('Could not find Geoman polygon layers for custom ordering')
        return
      }

      // Add filters to Geoman's default layers to exclude overlays
      // This lets Geoman handle regular features natively (better performance)
      try {
        this.map.setFilter(geomanFillLayer.id, ['!=', ['get', '_isMapOverlay'], true])
        this.map.setFilter(geomanLineLayer.id, ['!=', ['get', '_isMapOverlay'], true])
      } catch (error) {
        console.warn('Failed to set filters on Geoman layers:', error)
        return
      }

      // Create only 2 custom layers for overlays (not 4 total layers)
      // Insert them before Geoman's default layers so overlays render below regular features

      // Create overlay fill layer (transparent but clickable)
      this.map.addLayer({
        id: 'gm_main_overlay_fill',
        type: 'fill',
        source: 'gm_main',
        filter: ['==', ['get', '_isMapOverlay'], true],
        paint: {
          'fill-opacity': 0 // Invisible but clickable
        }
      }, geomanFillLayer.id) // Insert before default fill layer

      // Create overlay line layer (visible outline)
      this.map.addLayer({
        id: 'gm_main_overlay_line',
        type: 'line',
        source: 'gm_main',
        filter: ['==', ['get', '_isMapOverlay'], true],
        paint: { ...geomanLineLayer.paint }
      }, geomanFillLayer.id) // Insert before default fill layer

      // Move GeoTIFF layers to be below all polygon features
      moveGeoTIFFsBeforeGeoman(this.map)
    })
  }

  /**
   * Displays the features on the map.
   * @param {GeoJsonImportFeature[]} features - The GeoJSON feature to display on the map
   */
  #showFeatures (features) {
    if (!this.geoman || !this.map) {
      return
    }

    // Remove existing features
    this.geoman.features.forEach((feature) => {
      this.geoman?.features.delete(feature)
    })

    // Import new features to display on the map
    features.forEach((feature) => {
      if (feature?.properties?._isMapOverlay && this.#hiddenOverlayIds.has(feature.properties?._id)) {
        return
      }

      // Skip features with null/undefined geometry
      if (!feature?.geometry || !feature.geometry.coordinates.length) {
        console.warn('Skipping feature with null geometry:', feature)
        return
      }

      try {
        this.geoman?.features.importGeoJson(feature)
      } catch (error) {
        console.error('[showFeatures] Error importing feature:', error, feature)
      }
    })

    if (!this.initialFeaturesLoaded) {
      this.centerOnAllFeatures()
      this.initialFeaturesLoaded = true
    } else {
      editFeature(this.geoman, this.map, reactiveStore.selectedFeatureId.get(), reactiveStore.editMode.get())
    }
  }

  /**
   * Centers and zooms the map to fit the bounds of the features
   */
  async centerOnAllFeatures () {
    if (!this.map || !this.geoman) return

    // Collect all features into a FeatureCollection
    const features = []
    this.geoman.features.featureStore.forEach((entry) => {
      try {
        const gj = entry.getGeoJson()
        if (gj?.geometry) {
          features.push(gj)
        }
      } catch {
        // ignore invalid features
      }
    })

    if (features.length === 0) return

    // Use turf.bbox() to calculate bounding box
    const featureCollection = turf.featureCollection(features)
    const bbox = turf.bbox(featureCollection)

    // Convert to MapLibre LngLatBounds format: [minX, minY, maxX, maxY] -> [[minX, minY], [maxX, maxY]]
    const bounds = new ml.LngLatBounds(
      [bbox[0], bbox[1]],
      [bbox[2], bbox[3]]
    )

    this.map.fitBounds(bounds, {
      padding: 50,
      maxZoom: 18,
      duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1500
    })
  }

  /**
   * Gets the current center point of the map
   * @returns {[number, number] | null} The map center as [lng, lat] array, or null if map is not ready
   */
  getCenter () {
    if (!this.map) return null
    const center = this.map.getCenter()
    return [center.lng, center.lat]
  }

  /**
   * Zooms the map in or out
   * @param {'in' | 'out'} direction
   */
  zoom (direction) {
    if (!this.map) return
    const options = {
      animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
    if (direction === 'in') {
      this.map.zoomIn(options)
    } else if (direction === 'out') {
      this.map.zoomOut(options)
    }
  }

  /**
   * Changes the map tile type by recreating the map with a new style
   * @param {'os' | 'satellite'} tileType
   */
  #changeTileType (tileType) {
    if (!this.map) return
    this.#stopRotateSync()
    this.#removeMouseLeaveHandler()
    clearOverlayCornerCache()

    // Save current map state
    const center = this.map.getCenter()
    const zoom = this.map.getZoom()

    // Clean up snapping subscription
    if (this.snapCallback) {
      reactiveStore.snap.unsubscribe(this.snapCallback)
      this.snapCallback = null
    }

    // Clean up magic wand
    this.magicWand?.disable()
    this.magicWand = undefined

    // Remove old map and geoman
    this.map.remove()
    this.geoman = undefined

    // Create new map with the new style, preserving position
    const styleUrl = tileType === 'satellite' ? '/api/map-style-satellite' : '/api/map-style-os'
    this.#initMaplibre(styleUrl)

    // Restore map position after style loads
    this.map.once('style.load', () => {
      this.map?.setCenter(center)
      this.map?.setZoom(zoom)
    })

    // Re-initialise Geoman and restore features after it loads
    this.#initGeoman()
    const savedFeatures = reactiveStore.features.get()
    if (savedFeatures.length > 0) {
      this.map.once('gm:loaded', () => {
        if (this.geoman && this.map) {
          const overlayMaps = reactiveStore.overlayMaps.get()

          // Re-initialize GeoTIFF sources (they were destroyed with old map)
          initGeoTIFFs(this.map, overlayMaps)
          updateGeoTIFFOpacities(this.map, overlayMaps)

          // Restore features
          this.#showFeatures(savedFeatures)

          // Sync GeoTIFF overlays after features are imported (visual only — do not persist GCPs)
          syncGeoTIFFOverlaysFromFeatures(this.map, savedFeatures, false, null)

          // Re-establish mode handlers after map recreation
          const currentMode = reactiveStore.editMode.get()
          if (currentMode === 'rotate') {
            this.#startRotateSync()
          }
        }
      })
    }
  }

  #saveCutFeature () {
    const selectedId = reactiveStore.selectedFeatureId.get()
    if (!selectedId) return false
    const allFeatures = reactiveStore.features.get()
    let cutFeature
    this.geoman?.features.featureStore.forEach((entry) => {
      try {
        const geoJson = entry.getGeoJson()
        if (geoJson?.properties?._id === selectedId) {
          cutFeature = geoJson
        }
      } catch (error) {
        console.warn('Error getting GeoJSON from feature during cut:', error)
      }
    })
    if (!cutFeature) return false
    const coordsString = JSON.stringify(cutFeature.geometry.coordinates)
    if (this.#lastCutGeometry === coordsString) {
      return false
    }
    this.#lastCutGeometry = coordsString
    const updatedFeatures = allFeatures.map(feature =>
      feature.properties?._id === selectedId ? cutFeature : feature
    )
    reactiveStore.features.set(updatedFeatures)
    return true
  }

  #collectLiveFeatures () {
    const features = []
    this.geoman?.features.featureStore.forEach((entry) => {
      try {
        if (entry.shape !== 'vertex_marker' && entry.shape !== 'edge_marker' && entry.shape !== 'center_marker') {
          const gj = entry.getGeoJson()
          if (gj?.geometry) features.push(gj)
        }
      } catch {}
    })
    return features
  }

  #startRotateSync () {
    this.#stopRotateSync()
    const tick = () => {
      if (this.map) {
        const rotState = getRotationState()
        const features = this.#collectLiveFeatures()
        syncGeoTIFFOverlaysFromFeatures(this.map, features, false, rotState)
        this.#rafId = requestAnimationFrame(tick)
      } else {
        this.#rafId = null
      }
    }
    this.#rafId = requestAnimationFrame(tick)
  }

  #stopRotateSync () {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId)
      this.#rafId = null
    }
  }

  /**
   * Handles snap state changes
   * @param {boolean} snap
   */
  #handleSnapChange (snap) {
    if (snap) {
      this.geoman?.enableMode('helper', 'snapping')
      this.snapping?.enable()
    } else {
      this.geoman?.disableMode('helper', 'snapping')
      this.snapping?.disable()
    }
  }

  /**
   * Sets up mouseleave handler to complete drag/transform operations when mouse leaves canvas
   */
  #setupMouseLeaveHandler () {
    if (!this.map) return

    // Remove existing listener if any
    this.#removeMouseLeaveHandler()

    const container = this.map.getContainer()
    if (!container) return

    this.#mouseLeaveListener = () => {
      const canvas = container.querySelector('canvas')
      if (!canvas) return

      // Simulate both pointerup and mouseup events to complete the operation naturally
      // Dispatch to both canvas and document to ensure vertex markers in change mode receive events
      // Note: Document-level listeners will fire twice (once from bubble, once direct dispatch)
      // This double-dispatch is intentional and necessary for change mode to work correctly
      const pointerUpEvent = new PointerEvent('pointerup', {
        bubbles: true,
        cancelable: true,
        view: window
      })
      const mouseUpEvent = new MouseEvent('mouseup', {
        bubbles: true,
        cancelable: true,
        view: window
      })
      canvas.dispatchEvent(pointerUpEvent)
      canvas.dispatchEvent(mouseUpEvent)
      document.dispatchEvent(new PointerEvent('pointerup', { bubbles: true, cancelable: true, view: window }))
      document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window }))
    }

    container.addEventListener('mouseleave', this.#mouseLeaveListener)
  }

  /**
   * Removes the mouseleave handler
   */
  #removeMouseLeaveHandler () {
    if (!this.map || !this.#mouseLeaveListener) return

    const container = this.map.getContainer()
    if (container) {
      container.removeEventListener('mouseleave', this.#mouseLeaveListener)
    }
    this.#mouseLeaveListener = null
  }
}

customElements.define('base-map', BaseMap)
